import { css } from "@emotion/css";
import { Badge, Callout, Checkbox, RadioButtonNew, RadioGroupNew, MarkdownEditor, Tooltip } from "@octopusdeploy/design-system-components";
import { ExclamationTriangleIcon } from "@octopusdeploy/design-system-icons";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { GitRef, TemplateResourceBase } from "@octopusdeploy/octopus-server-client";
import { BranchesMatch } from "@octopusdeploy/octopus-server-client";
import semver from "semver/preload";
import { TemplateBanner } from "~/areas/platformHub/TemplateBanner";
import { GitRefChip } from "~/areas/projects/components/Releases/GitRefChip/GitRefChip";
import { repository } from "~/clientInstance";
import { PlatformHubTemplateType } from "../platformHubHelpers";
export interface PublishDialogTemplate extends TemplateResourceBase {
}
export interface PublishDialogVersion {
    Version: string;
    IsPreRelease: boolean;
}
interface PublishTemplateDialogProps {
    template: PublishDialogTemplate;
    templateType: PlatformHubTemplateType;
    gitRef: GitRef;
    defaultBranch: string;
    publishedVersions: PublishDialogVersion[];
    versionToPublish: string;
    setVersionToPublish: (version: string) => void;
    latestNonPreReleaseVersion: PublishDialogVersion | undefined;
    isPreRelease: boolean;
    setIsPreRelease: (isPreRelease: boolean) => void;
    releaseType: ReleaseType;
    setReleaseType: (releaseType: ReleaseType) => void;
    releaseNotes?: string;
    setReleaseNotes?: (releaseNotes: string) => void;
    hasUnsupportedSteps: boolean;
}
export function PublishTemplateDialog({ template, templateType, gitRef, defaultBranch, publishedVersions, versionToPublish, setVersionToPublish, latestNonPreReleaseVersion, isPreRelease, setIsPreRelease, releaseType, setReleaseType, releaseNotes, setReleaseNotes, hasUnsupportedSteps, }: PublishTemplateDialogProps) {
    const onReleaseTypeChanged = (newReleaseType: ReleaseType) => {
        setReleaseType(newReleaseType);
        updatePublishVersion(newReleaseType, isPreRelease);
    };
    const onIsPreReleaseChanged = (newIsPreRelease: boolean) => {
        setIsPreRelease(newIsPreRelease);
        updatePublishVersion(releaseType, newIsPreRelease);
    };
    const updatePublishVersion = (releaseType: ReleaseType, isPreRelease: boolean) => {
        if (isPreRelease) {
            if (!latestNonPreReleaseVersion) {
                const latestPreReleaseVersion: PublishDialogVersion | undefined = publishedVersions[0];
                if (!latestPreReleaseVersion) {
                    setVersionToPublish(`${defaultVersion}-prerelease.0`);
                    return;
                }
                setVersionToPublish(semver.inc(latestPreReleaseVersion.Version, "prerelease", "prerelease") ?? defaultVersion);
                return;
            }
            const latestVersion = getLatestPreReleaseForReleaseType(publishedVersions, releaseType, latestNonPreReleaseVersion) ?? latestNonPreReleaseVersion;
            setVersionToPublish(incrementPreReleaseVersionByReleaseType(latestVersion, releaseType) ?? defaultVersion);
        }
        else {
            const latestVersion = publishedVersions.find((v) => !semver.prerelease(v.Version));
            if (!latestVersion) {
                setVersionToPublish(defaultVersion);
                return;
            }
            setVersionToPublish(incrementVersionByReleaseType(latestVersion, releaseType) ?? defaultVersion);
        }
    };
    const latestVersion = isPreRelease ? (getLatestPreReleaseForReleaseType(publishedVersions, releaseType, latestNonPreReleaseVersion) ?? latestNonPreReleaseVersion) : latestNonPreReleaseVersion;
    const versionInfo = (<div>
            {latestVersion ? (<span className={styles.versionContainer}>
                    <span>{latestVersion.Version}</span>
                    <span>→</span>
                    <Badge size={"medium"} label={versionToPublish} variant={"info"} statusAnimation={true}/>
                </span>) : (<span className={styles.versionContainer}>{versionToPublish}</span>)}
        </div>);
    const gitRefAccessory = (<div className={styles.accessoryContainer}>
            {!BranchesMatch(gitRef, defaultBranch) && (<Tooltip content={"Publishing from the non-default branch"}>
                    <ExclamationTriangleIcon size={24} color={themeTokens.color.icon.warning}/>
                </Tooltip>)}
            <GitRefChip vcsRef={{ GitRef: gitRef }} maxGitRefLength={25}/>
        </div>);
    const getPreReleaseNote = (templateType: PlatformHubTemplateType) => {
        switch (templateType) {
            case PlatformHubTemplateType.ProjectTemplate:
                return "Recommended if you are still testing the template. Version names, including pre-release versions, will be displayed at the project template selection stage.";
            case PlatformHubTemplateType.ProcessTemplate:
                return "Recommended if you are still testing the template. Pre-release templates are flagged in the Process Editor step selector.";
        }
    };
    return (<div className={styles.dialogContainer}>
            {hasUnsupportedSteps ? (<Callout type="warning" title="This template contains unsupported steps.">
                    Unsupported steps must be removed before the template can be published.
                </Callout>) : null}
            <TemplateBanner iconLink={repository.Icons.getIconUrl(template.Icon)} heading={template.Name} subHeading={versionInfo} accessories={gitRefAccessory}/>
            <div className={styles.radioButtonContainer}>
                <h3 className={styles.releaseTypeHeading}>Release type</h3>
                {latestNonPreReleaseVersion ? null : <div className={styles.firstVersionNote}>The first version of a template will always be a major release.</div>}
                <RadioGroupNew value={releaseType} onChange={(rt) => onReleaseTypeChanged(rt)} label="">
                    <RadioButtonNew name="Major" value="Major" label="Major changes (breaking)" disabled={!latestNonPreReleaseVersion}/>
                    <RadioButtonNew name="Minor" value="Minor" label="Minor changes (non-breaking)" disabled={!latestNonPreReleaseVersion}/>
                    <RadioButtonNew name="Patch" value="Patch" label="Patch (bug fixes)" disabled={!latestNonPreReleaseVersion}/>
                </RadioGroupNew>
            </div>
            {releaseNotes !== undefined && setReleaseNotes !== undefined && (<div className={styles.releaseNotesContainer}>
                    <h3 className={styles.releaseNotesHeading}>Release notes (optional)</h3>
                    <div className={styles.releaseNotesSummary}>Write a summary of what has changed, including bugs fixed and new features. These notes are displayed in the template update message within the process editor</div>
                    <div className={styles.markdownEditorContainer}>
                        <MarkdownEditor restrictHeight={true} value={releaseNotes} onChange={setReleaseNotes}/>
                    </div>
                </div>)}
            <div>
                <h4 className={styles.preReleaseHeading}>Flag as pre-release (optional)</h4>
                <Checkbox value={isPreRelease} onChange={onIsPreReleaseChanged} label="Flag as pre-release" noMargin={true} note={getPreReleaseNote(templateType)}/>
            </div>
        </div>);
}
export type ReleaseType = "Major" | "Minor" | "Patch";
const defaultVersion = "1.0.0";
export function getLatestPreReleaseForReleaseType(publishedVersions: PublishDialogVersion[], releaseType: ReleaseType, latestNonPreReleaseVersion: PublishDialogVersion | undefined): PublishDialogVersion | undefined {
    if (!latestNonPreReleaseVersion) {
        return publishedVersions[0];
    }
    switch (releaseType) {
        case "Major":
            return publishedVersions.find((v) => semver.diff(v.Version, latestNonPreReleaseVersion.Version) === "premajor");
        case "Minor":
            return publishedVersions.find((v) => semver.diff(v.Version, latestNonPreReleaseVersion.Version) === "preminor");
        case "Patch":
            return publishedVersions.find((v) => semver.diff(v.Version, latestNonPreReleaseVersion.Version) === "prepatch");
    }
}
export function incrementPreReleaseVersionByReleaseType(latestVersion: PublishDialogVersion, releaseType: ReleaseType) {
    if (semver.prerelease(latestVersion.Version)) {
        return semver.inc(latestVersion.Version, "prerelease", "prerelease");
    }
    switch (releaseType) {
        case "Major":
            return semver.inc(latestVersion.Version, "premajor", "prerelease");
        case "Minor":
            return semver.inc(latestVersion.Version, "preminor", "prerelease");
        case "Patch":
            return semver.inc(latestVersion.Version, "prepatch", "prerelease");
    }
}
export function incrementVersionByReleaseType(latestVersion: PublishDialogVersion, releaseType: ReleaseType) {
    switch (releaseType) {
        case "Major":
            return semver.inc(latestVersion.Version, "major");
        case "Minor":
            return semver.inc(latestVersion.Version, "minor");
        case "Patch":
            return semver.inc(latestVersion.Version, "patch");
    }
}
const styles = {
    dialogContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[32],
    }),
    versionContainer: css({
        display: "flex",
        gap: space[6],
        alignItems: "center",
        font: text.body.regular.small,
    }),
    releaseTypeHeading: css({
        font: text.heading.small,
        margin: `0 0 ${space[12]} 0`,
    }),
    releaseNotesHeading: css({
        font: text.heading.small,
        margin: `0 0 ${space[6]} 0`,
    }),
    preReleaseHeading: css({
        font: text.heading.xSmall,
        color: themeTokens.color.text.tertiary,
        textTransform: "uppercase",
        margin: `0 0 ${space[16]} 0`,
    }),
    firstVersionNote: css({
        font: text.body.regular.medium,
        margin: `0 0 ${space[12]} 0`,
    }),
    releaseNotesSummary: css({
        font: text.body.regular.xSmall,
        color: themeTokens.color.text.tertiary,
        margin: `0 0 ${space[12]} 0`,
    }),
    releaseNotesContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[6],
        marginBottom: space[48],
    }),
    markdownEditorContainer: css({
        "& > div": {
            maxHeight: "200px",
            overflow: "visible",
        },
        "& textarea": {
            minHeight: "100px !important",
            maxHeight: "120px !important",
            resize: "none",
            overflow: "auto !important",
            padding: `${space[8]} !important`,
        },
    }),
    accessoryContainer: css({
        display: "flex",
        alignItems: "center",
        gap: space[2],
    }),
    radioButtonContainer: css({
        padding: space[6],
    }),
};
